import { Link } from "@/src/common/components/Link"
import { PORTRAIT_TABLET_MAX_WIDTH } from "@/src/common/config"
import useDisableScroll from "@/src/common/hooks/useDisableScroll"
import { DEKSTOP_SINGLE_COLUMN_TOPIC_CATEGORIES } from "@/src/sb/business-layer/config"
import { NavigationMenuItem } from "@/src/sb/business-layer/types"
import Logo from "@/src/sb/components/Logo"
import { useWindowSize } from "@/src/sb/hooks/useWindowSize"
import classNames from "classnames"
import { groupBy } from "lodash"
import { Fragment, MouseEventHandler, ReactNode, useRef } from "react"

const MenuHeader = ({
  children,
  extraClassName
}: {
  children: string
  extraClassName?: string
}) => {
  return (
    <h4 className={classNames("text-xl font-bold", extraClassName)}>
      {children}
    </h4>
  )
}

type CategoryMenuProps = {
  category?: string
  menu: Array<{ href: string } & NavigationMenuItem>
  className?: string
  onMenuItemClick?: (
    menuItem: Pick<
      NavigationMenuItem,
      "label" | "destination" | "category_name"
    >
  ) => void
}

const MobileCategoryMenu = ({
  category = "",
  menu,
  onMenuItemClick,
  extraHeaderClassName
}: { extraHeaderClassName?: string } & CategoryMenuProps) => {
  return (
    <>
      {category ? (
        <MenuHeader extraClassName={extraHeaderClassName}>
          {category}
        </MenuHeader>
      ) : null}
      {menu.map(({ label, destination, href, category_name }) => (
        <Link
          key={destination}
          className={"text-sm"}
          href={href}
          onClick={() =>
            onMenuItemClick?.({
              label,
              destination,
              category_name
            })
          }
        >
          {label}
        </Link>
      ))}
    </>
  )
}

const DesktopCategoryMenu = ({
  category = "",
  menu,
  className = "",
  onMenuItemClick
}: CategoryMenuProps) => {
  const leftColumn: Array<ReactNode> = []
  const rightColumn: Array<ReactNode> = []

  menu.forEach(({ label, destination, href, category_name }, idx) => {
    const topicPos = idx + 1
    const topicJSX = (
      <Link
        href={href}
        key={destination}
        className={"block text-sm"}
        onClick={() =>
          onMenuItemClick?.({
            label,
            destination,
            category_name
          })
        }
      >
        {label}
      </Link>
    )

    if (topicPos > 1 && !(topicPos % 2)) {
      rightColumn.push(topicJSX)

      return
    }

    leftColumn.push(topicJSX)
  })

  return (
    <section className={className}>
      {category ? (
        <MenuHeader extraClassName={"mb-6"}>{category}</MenuHeader>
      ) : null}
      <section
        className={classNames(
          "gap-4",
          DEKSTOP_SINGLE_COLUMN_TOPIC_CATEGORIES.includes(
            category.toLowerCase()
          ) || !category
            ? "lg:space-y-4"
            : "inline-flex"
        )}
      >
        <div className={"space-y-4"}>{leftColumn}</div>
        <div className={"space-y-4"}>{rightColumn}</div>
      </section>
    </section>
  )
}

const CATEGORY_TOPIC_CLASS_NAMES =
  "lg:pl-5 lg:first:pl-0 pt-6 first:pt-0 lg:pt-0 px-10 lg:px-0"

const MenuDropdown = ({
  menu,
  onClose,
  extraClassName,
  categorizeMenuItem,
  onMenuItemClick
}: {
  menu: Array<{ href: string } & NavigationMenuItem>
  onClose?: () => void
  extraClassName?: string
  categorizeMenuItem?: boolean
  onMenuItemClick?: (
    menuItem: Pick<
      NavigationMenuItem,
      "label" | "destination" | "category_name"
    >
  ) => void
}) => {
  useDisableScroll()
  const [, width] = useWindowSize()

  const isTablet = width <= PORTRAIT_TABLET_MAX_WIDTH
  const categorizedTopics = categorizeMenuItem
    ? groupBy(menu, ({ category_name }) => category_name)
    : {}
  const rootNode = useRef<HTMLDivElement | null>(null)

  const handleOutsideClick: MouseEventHandler<HTMLDivElement> = (e) => {
    const clickedNode = e.target
    if (rootNode.current !== clickedNode) return

    onClose?.()
  }

  return (
    <section
      ref={rootNode}
      onClick={handleOutsideClick}
      className={classNames(
        "fixed top-0 md:pt-14 md:pb-8 md:px-4 lg:px-16 xl:px-32 left-0 z-40",
        "w-full h-full bg-[rgba(0,0,0,0.1)] flex justify-center items-start",
        extraClassName
      )}
    >
      <div
        className={
          "w-full inline-block bg-white font-jakarta md:rounded-lg md:border md:border-gray-10 md:shadow-lg h-full md:h-auto md:max-h-full overflow-y-auto overflow-x-hidden relative"
        }
      >
        <header
          className={
            "lg:hidden flex items-center py-4 pr-3 border-b border-gray-10 sticky top-0 bg-white z-50"
          }
        >
          <section className={"grow"}>
            <Logo type="black" />
          </section>

          <section>
            <button onClick={onClose}>
              <svg width="24" height="25" viewBox="0 0 24 25" fill="none">
                <rect y="0.5" width="24" height="24" fill="#F3F4F6" />
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M16.8243 7.67574C17.0586 7.91005 17.0586 8.28995 16.8243 8.52426L12.8488 12.5L16.8243 16.4757C17.0586 16.7101 17.0586 17.0899 16.8243 17.3243C16.5899 17.5586 16.2101 17.5586 15.9757 17.3243L12 13.3488L8.02426 17.3243C7.78995 17.5586 7.41005 17.5586 7.17574 17.3243C6.94142 17.0899 6.94142 16.7101 7.17574 16.4757L11.1512 12.5L7.17574 8.52426C6.94142 8.28995 6.94142 7.91005 7.17574 7.67574C7.41005 7.44142 7.78995 7.44142 8.02426 7.67574L12 11.6512L15.9757 7.67574C16.2101 7.44142 16.5899 7.44142 16.8243 7.67574Z"
                  fill="#121417"
                />
              </svg>
            </button>
          </section>
        </header>

        <div
          className={classNames(
            [categorizeMenuItem ? "py-12" : "py-4"],
            [
              isTablet
                ? "inline-grid gap-4 mx-9"
                : "lg:flex lg:justify-center lg:gap-5 lg:px-5 lg:divide-x-[1px] space-y-6 lg:space-y-0"
            ]
          )}
        >
          {categorizeMenuItem ? (
            Object.keys(categorizedTopics).map((category, idx) => {
              const categoryTopics = categorizedTopics[category]
              const isNotFirstListItem = idx > 0

              return isTablet ? (
                <Fragment key={category}>
                  {isNotFirstListItem ? (
                    <section className="relative col-span-2 mt-2">
                      <div className="border-t border-t-y-[1px] lg:border-t-y-0 w-screen absolute left-0 right-0 -mx-8" />
                    </section>
                  ) : null}

                  <MobileCategoryMenu
                    extraHeaderClassName={classNames("col-span-2 mb-2", {
                      "mt-2": isNotFirstListItem
                    })}
                    category={category}
                    onMenuItemClick={onMenuItemClick}
                    menu={categoryTopics}
                  />
                </Fragment>
              ) : (
                <DesktopCategoryMenu
                  key={category}
                  category={category}
                  onMenuItemClick={onMenuItemClick}
                  menu={categoryTopics}
                  className={CATEGORY_TOPIC_CLASS_NAMES}
                />
              )
            })
          ) : (
            <DesktopCategoryMenu
              menu={menu}
              onMenuItemClick={onMenuItemClick}
              className={CATEGORY_TOPIC_CLASS_NAMES}
            />
          )}
        </div>
      </div>
    </section>
  )
}

export default MenuDropdown
